import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { Button } from '@/ui/components/common/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/forms/Select'

export type LiveSortOrder = 'newest' | 'oldest'

interface LiveHeaderProps {
  sortOrder: LiveSortOrder
  onSortOrderChange: (value: LiveSortOrder) => void
  autoRefresh: boolean
  onAutoRefreshToggle: () => void
  onRefresh: () => void | Promise<void>
}

/**
 * Top header of the Live page: title, sort selector, manual refresh button
 * and auto-refresh toggle. Owns no data — purely presentational; state lives
 * in the parent page so it can be shared with the accordion section.
 */
export function LiveHeader({
  sortOrder,
  onSortOrderChange,
  autoRefresh,
  onAutoRefreshToggle,
  onRefresh,
}: LiveHeaderProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Select
          value={sortOrder}
          onValueChange={(value) => {
            if (value === 'newest' || value === 'oldest') onSortOrderChange(value)
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="oldest">{t('admin.live_oldest_first')}</SelectItem>
            <SelectItem value="newest">{t('admin.live_newest_first')}</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => void onRefresh()}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          {t('admin.live_refresh')}
        </Button>

        <Button
          variant={autoRefresh ? 'primary' : 'outline'}
          size="sm"
          onClick={onAutoRefreshToggle}
        >
          {autoRefresh ? t('admin.live_auto_on') : t('admin.live_auto_off')}
        </Button>
      </div>
    </div>
  )
}
